import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const EMPTY_FORM = {
  funeral_preference: 'no_preference',
  funeral_details: '',
  funeral_home: '',
  organ_donor: false,
  organ_donor_details: '',
  service_preferences: '',
  additional_wishes: '',
  letters: [],
};

export default function Wishes({ session }) {
  const [vault, setVault] = useState(null);
  const [wishes, setWishes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [newLetter, setNewLetter] = useState({ recipient_name: '', message: '' });
  const [showLetterForm, setShowLetterForm] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [session]);

  const load = async () => {
    const { data: vaultData } = await supabase.from('vaults').select('id').eq('owner_id', session.user.id).single();
    setVault(vaultData);
    if (vaultData) {
      const { data } = await supabase.from('final_wishes').select('*').eq('vault_id', vaultData.id).single();
      if (data) {
        setWishes(data);
        setForm({
          funeral_preference: data.funeral_preference || 'no_preference',
          funeral_details: data.funeral_details || '',
          funeral_home: data.funeral_home || '',
          organ_donor: data.organ_donor || false,
          organ_donor_details: data.organ_donor_details || '',
          service_preferences: data.service_preferences || '',
          additional_wishes: data.additional_wishes || '',
          letters: data.letters || [],
        });
      }
    }
    setLoading(false);
  };

  const handleSave = async (e) => {
    e.preventDefault(); setError(''); setSaving(true);
    const payload = { ...form, vault_id: vault.id, owner_id: session.user.id };
    if (wishes) {
      const { error: err } = await supabase.from('final_wishes').update({ ...payload, updated_at: new Date().toISOString() }).eq('id', wishes.id);
      if (err) { setError(err.message); setSaving(false); return; }
    } else {
      const { error: err } = await supabase.from('final_wishes').insert(payload);
      if (err) { setError(err.message); setSaving(false); return; }
    }
    setSaving(false);
    setSuccess('Your final wishes have been saved.');
    setTimeout(() => setSuccess(''), 3000);
    load();
  };

  const addLetter = () => {
    if (!newLetter.recipient_name || !newLetter.message) return;
    setForm(prev => ({ ...prev, letters: [...prev.letters, { ...newLetter, id: Date.now() }] }));
    setNewLetter({ recipient_name: '', message: '' });
    setShowLetterForm(false);
  };

  const removeLetter = (id) => {
    setForm(prev => ({ ...prev, letters: prev.letters.filter(l => l.id !== id) }));
  };

  const style = `
    .pg { padding: 32px; max-width: 860px; margin: 0 auto; }
    .pg-header { margin-bottom: 28px; }
    .pg-title { font-family: 'Fraunces', serif; font-weight: 900; font-size: 32px; color: #1E1040; line-height: 1.05; margin-bottom: 8px; }
    .pg-title em { font-style: italic; color: #8B5CF6; }
    .pg-sub { font-size: 14px; color: #6B7280; line-height: 1.6; max-width: 560px; }
    .pg-gentle { background: linear-gradient(135deg, rgba(139,92,246,0.05), rgba(236,72,153,0.03)); border: 1px solid rgba(139,92,246,0.12); border-radius: 16px; padding: 20px 24px; margin-bottom: 28px; font-size: 14px; color: #374151; line-height: 1.7; }
    .pg-gentle strong { color: #1E1040; }
    .pg-error { background: rgba(239,68,68,0.06); border: 1px solid rgba(239,68,68,0.2); border-radius: 10px; padding: 12px 16px; font-size: 13px; color: #DC2626; margin-bottom: 16px; }
    .pg-success { background: rgba(34,197,94,0.06); border: 1px solid rgba(34,197,94,0.2); border-radius: 10px; padding: 12px 16px; font-size: 13px; color: #16A34A; margin-bottom: 16px; }

    .pg-section { background: #fff; border: 1.5px solid #EAE5F5; border-radius: 20px; padding: 28px; margin-bottom: 20px; box-shadow: 0 2px 12px rgba(139,92,246,0.05); }
    .pg-section-title { font-family: 'Fraunces', serif; font-weight: 900; font-size: 18px; color: #1E1040; margin-bottom: 20px; display: flex; align-items: center; gap: 10px; }
    .pg-section-icon { font-size: 20px; }

    .pg-label { font-size: 12px; font-weight: 700; color: #374151; margin-bottom: 6px; display: block; }
    .pg-field { margin-bottom: 16px; }
    .pg-input, .pg-textarea, .pg-select { padding: 11px 14px; border: 1.5px solid #E5E7EB; border-radius: 10px; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; color: #1E1040; background: #FAFAFA; outline: none; transition: border-color 0.2s; width: 100%; }
    .pg-input:focus, .pg-textarea:focus, .pg-select:focus { border-color: #8B5CF6; background: #fff; }
    .pg-textarea { resize: vertical; min-height: 100px; }
    .pg-two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
    @media(max-width:600px){ .pg-two-col { grid-template-columns: 1fr; } }

    /* FUNERAL OPTIONS */
    .pg-funeral-opts { display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 16px; }
    .pg-funeral-opt { flex: 1; min-width: 120px; padding: 14px 16px; border-radius: 12px; border: 1.5px solid #EAE5F5; background: #FDFCFF; cursor: pointer; text-align: center; transition: all 0.15s; font-family: 'Plus Jakarta Sans', sans-serif; }
    .pg-funeral-opt:hover { border-color: rgba(139,92,246,0.3); }
    .pg-funeral-opt.selected { border-color: #8B5CF6; background: rgba(139,92,246,0.06); }
    .pg-funeral-opt-icon { font-size: 24px; display: block; margin-bottom: 6px; }
    .pg-funeral-opt-label { font-size: 13px; font-weight: 600; color: #374151; }
    .pg-funeral-opt.selected .pg-funeral-opt-label { color: #7C3AED; }

    /* TOGGLE */
    .pg-toggle-row { display: flex; align-items: center; gap: 14px; margin-bottom: 12px; }
    .pg-toggle { position: relative; width: 44px; height: 24px; flex-shrink: 0; }
    .pg-toggle input { opacity: 0; width: 0; height: 0; }
    .pg-toggle-slider { position: absolute; inset: 0; background: #D1D5DB; border-radius: 100px; cursor: pointer; transition: background 0.2s; }
    .pg-toggle-slider::before { content: ''; position: absolute; width: 18px; height: 18px; background: #fff; border-radius: 50%; top: 3px; left: 3px; transition: transform 0.2s; box-shadow: 0 1px 3px rgba(0,0,0,0.2); }
    .pg-toggle input:checked + .pg-toggle-slider { background: #8B5CF6; }
    .pg-toggle input:checked + .pg-toggle-slider::before { transform: translateX(20px); }
    .pg-toggle-label { font-size: 14px; font-weight: 600; color: #374151; }

    /* LETTERS */
    .pg-letters { display: flex; flex-direction: column; gap: 12px; margin-bottom: 16px; }
    .pg-letter-card { background: linear-gradient(145deg, #FDFCFF, #FAF8FF); border: 1.5px solid #EAE5F5; border-radius: 14px; padding: 18px; }
    .pg-letter-recipient { font-size: 14px; font-weight: 700; color: #1E1040; margin-bottom: 6px; display: flex; align-items: center; gap: 8px; justify-content: space-between; }
    .pg-letter-msg { font-size: 13px; color: #6B7280; line-height: 1.6; white-space: pre-wrap; }
    .pg-letter-remove { font-size: 12px; color: #9CA3AF; background: none; border: none; cursor: pointer; padding: 2px 8px; border-radius: 6px; transition: color 0.15s; }
    .pg-letter-remove:hover { color: #EF4444; }
    .pg-letter-form { background: #F8F7FB; border: 1.5px solid #EAE5F5; border-radius: 14px; padding: 18px; }
    .pg-letter-form-title { font-size: 14px; font-weight: 700; color: #1E1040; margin-bottom: 14px; }
    .pg-add-letter-btn { display: flex; align-items: center; gap: 6px; padding: 10px 18px; border: 1.5px dashed #C4B5FD; border-radius: 100px; background: transparent; color: #7C3AED; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.15s; }
    .pg-add-letter-btn:hover { background: rgba(139,92,246,0.04); border-color: #8B5CF6; }
    .pg-letter-actions { display: flex; gap: 8px; margin-top: 12px; justify-content: flex-end; }
    .pg-letter-cancel { padding: 8px 16px; border: 1.5px solid #EDE9E3; border-radius: 100px; background: transparent; color: #6B7280; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; font-weight: 600; cursor: pointer; }
    .pg-letter-save { padding: 8px 18px; border: none; border-radius: 100px; background: linear-gradient(135deg, #8B5CF6, #38BDF8); color: #fff; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; font-weight: 700; cursor: pointer; }

    /* SAVE BUTTON */
    .pg-save-wrap { display: flex; justify-content: flex-end; margin-top: 8px; }
    .pg-save-btn { display: flex; align-items: center; gap: 8px; background: linear-gradient(135deg, #8B5CF6, #38BDF8); color: #fff; border: none; border-radius: 100px; padding: 14px 32px; font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 700; font-size: 15px; cursor: pointer; box-shadow: 0 4px 16px rgba(139,92,246,0.3); transition: opacity 0.2s; }
    .pg-save-btn:hover { opacity: 0.88; }
    .pg-save-btn:disabled { opacity: 0.6; cursor: not-allowed; }
  `;

  if (loading) return <div style={{padding: 32, color: '#6B7280', fontSize: 14}}>Loading...</div>;

  return (
    <>
      <style>{style}</style>
      <div className="pg">
        <div className="pg-header">
          <h1 className="pg-title">Final <em>Wishes</em></h1>
          <p className="pg-sub">Your preferences for end-of-life arrangements and personal letters to the people you love.</p>
        </div>

        <div className="pg-gentle">
          <strong>This section is a gift to your family.</strong> You don't have to fill everything out at once — even a few sentences about your preferences will spare your family from having to guess during an already difficult time. Write as much or as little as feels right.
        </div>

        {error && <div className="pg-error">{error}</div>}
        {success && <div className="pg-success">✓ {success}</div>}

        <form onSubmit={handleSave}>

          {/* FUNERAL PREFERENCES */}
          <div className="pg-section">
            <div className="pg-section-title"><span className="pg-section-icon">🕊️</span> Funeral Preferences</div>
            <div className="pg-funeral-opts">
              {[
                { key: 'burial',       icon: '⚰️', label: 'Burial' },
                { key: 'cremation',    icon: '🏺', label: 'Cremation' },
                { key: 'no_preference',icon: '🤍', label: 'No Preference' },
                { key: 'other',        icon: '✏️', label: 'Other' },
              ].map(opt => (
                <button key={opt.key} type="button"
                  className={`pg-funeral-opt${form.funeral_preference === opt.key ? ' selected' : ''}`}
                  onClick={() => setForm({...form, funeral_preference: opt.key})}>
                  <span className="pg-funeral-opt-icon">{opt.icon}</span>
                  <span className="pg-funeral-opt-label">{opt.label}</span>
                </button>
              ))}
            </div>
            <div className="pg-field">
              <label className="pg-label">Details about your preference</label>
              <textarea className="pg-textarea" placeholder="Any specific wishes about the arrangements, location, style of service..." value={form.funeral_details} onChange={e => setForm({...form, funeral_details: e.target.value})} />
            </div>
            <div className="pg-field">
              <label className="pg-label">Preferred funeral home (if any)</label>
              <input className="pg-input" type="text" placeholder="Name and location of funeral home" value={form.funeral_home} onChange={e => setForm({...form, funeral_home: e.target.value})} />
            </div>
          </div>

          {/* ORGAN DONATION */}
          <div className="pg-section">
            <div className="pg-section-title"><span className="pg-section-icon">❤️</span> Organ Donation</div>
            <div className="pg-toggle-row">
              <label className="pg-toggle">
                <input type="checkbox" checked={form.organ_donor} onChange={e => setForm({...form, organ_donor: e.target.checked})} />
                <span className="pg-toggle-slider" />
              </label>
              <span className="pg-toggle-label">{form.organ_donor ? 'I am an organ donor' : 'I am not an organ donor'}</span>
            </div>
            {form.organ_donor && (
              <div className="pg-field">
                <label className="pg-label">Additional details</label>
                <textarea className="pg-textarea" style={{minHeight: 60}} placeholder="Any specific wishes or restrictions..." value={form.organ_donor_details} onChange={e => setForm({...form, organ_donor_details: e.target.value})} />
              </div>
            )}
          </div>

          {/* SERVICE PREFERENCES */}
          <div className="pg-section">
            <div className="pg-section-title"><span className="pg-section-icon">🌸</span> Service Preferences</div>
            <div className="pg-field">
              <label className="pg-label">What kind of service would you like?</label>
              <textarea className="pg-textarea" placeholder="Religious or non-religious, large or intimate, celebration of life, specific music, readings, people you'd like to speak..." value={form.service_preferences} onChange={e => setForm({...form, service_preferences: e.target.value})} />
            </div>
            <div className="pg-field">
              <label className="pg-label">Any other wishes or instructions</label>
              <textarea className="pg-textarea" placeholder="Charitable donations in lieu of flowers, specific items to be distributed, anything else you want your family to know..." value={form.additional_wishes} onChange={e => setForm({...form, additional_wishes: e.target.value})} />
            </div>
          </div>

          {/* LETTERS */}
          <div className="pg-section">
            <div className="pg-section-title"><span className="pg-section-icon">💌</span> Letters to Loved Ones</div>
            <p style={{fontSize: 14, color: '#6B7280', marginBottom: 20, lineHeight: 1.6}}>Write personal messages to the people who matter most. These will be kept private until your vault is opened.</p>

            {form.letters.length > 0 && (
              <div className="pg-letters">
                {form.letters.map((letter, i) => (
                  <div key={letter.id || i} className="pg-letter-card">
                    <div className="pg-letter-recipient">
                      <span>💌 To: <strong>{letter.recipient_name}</strong></span>
                      <button type="button" className="pg-letter-remove" onClick={() => removeLetter(letter.id || i)}>Remove</button>
                    </div>
                    <div className="pg-letter-msg">{letter.message}</div>
                  </div>
                ))}
              </div>
            )}

            {showLetterForm ? (
              <div className="pg-letter-form">
                <div className="pg-letter-form-title">Write a Letter</div>
                <div className="pg-field">
                  <label className="pg-label">To</label>
                  <input className="pg-input" type="text" placeholder="My daughter Sarah" value={newLetter.recipient_name} onChange={e => setNewLetter({...newLetter, recipient_name: e.target.value})} />
                </div>
                <div className="pg-field">
                  <label className="pg-label">Your message</label>
                  <textarea className="pg-textarea" style={{minHeight: 140}} placeholder="Write whatever is in your heart..." value={newLetter.message} onChange={e => setNewLetter({...newLetter, message: e.target.value})} />
                </div>
                <div className="pg-letter-actions">
                  <button type="button" className="pg-letter-cancel" onClick={() => { setShowLetterForm(false); setNewLetter({ recipient_name: '', message: '' }); }}>Cancel</button>
                  <button type="button" className="pg-letter-save" onClick={addLetter}>Add Letter</button>
                </div>
              </div>
            ) : (
              <button type="button" className="pg-add-letter-btn" onClick={() => setShowLetterForm(true)}>
                + Write a Letter
              </button>
            )}
          </div>

          <div className="pg-save-wrap">
            <button type="submit" className="pg-save-btn" disabled={saving}>
              {saving ? 'Saving...' : '💾 Save My Wishes'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
